// The changing rooms, Play Now and Royale, as the server describes them
// (edge-server directors/ and server.js playNowDirector / royaleDirector). A
// changing room sends a `round_setup` before every round (the roster, map and
// rules may all be new), says what the next round will be on `game_countdown`
// (`next`), and what the finished round meant on `game_over`.
import { NO_POWERUPS_MODE } from "core/src/playstyles.js";

export type DynamicKind = "playnow" | "royale";
export type ArenaShape = "Stadium" | "Rectangle";
export type SizeKey = "small" | "medium" | "large" | "huge";
export type RoyaleStage = "huge" | "large" | "medium" | "small" | "final";

// `round_setup`: the round about to start (also the round in progress, for
// someone who drops in).
export interface RoundSetup {
  dynamic: DynamicKind;
  mode: string;          // a round mode's name (core playstyles.js ROUND_MODES)
  mapDesc: any;
  sizeKey: SizeKey;
  shape: ArenaShape;
  round: number;         // 1-based, since the room opened
  scoreReset: boolean;   // the score starts over with this round (a new set, the Royale final)
  // Play Now: where the round sits in its set
  set?: number;
  setRound?: number;
  setRounds?: number;
  nextMode?: string;     // Play Now: the mode of the round after this one
  // Royale
  number?: number;       // "Royale #214"
  stage?: RoyaleStage;
  alive?: number;        // players in this round
  // The Royale is played in stages of a few full rounds; when a stage's last
  // round is over the bottom of its table goes out (edge-server directors/royale.js).
  stageNo?: number;      // 1-based
  stages?: number;       // how many stages before the final
  stageRound?: number;   // 1-based, within the stage
  stageRounds?: number;  // rounds a stage
  out?: number;          // how many go out when this stage ends (0 in the final)
  cut?: boolean;         // this round is the stage's last: they go out after it
  fields?: number[];     // players at the start of each stage, the two finalists last: [30, 23, ..., 2]
  final?: boolean;
  finalWins?: number;    // the final is first to this many rounds
  size?: number;         // how many started
  royaleRound?: number;  // 1-based
  openingMs?: number;    // round 1: the round starts this long after its setup (the opening plays meanwhile)
}

// `game_countdown.next`: the round after the pause.
export interface NextRound extends Partial<RoundSetup> {
  sizeChange?: "bigger" | "smaller" | null; // Play Now: the map changes size
  setOver?: boolean;                        // Play Now: the finished round closed a set
  stageChanged?: boolean;                   // Royale: the map shrinks
}

// Extra fields on `game_over`.
export interface RoundResult {
  // Play Now, when a set ends: the podium
  setOver?: boolean;
  standings?: { id: string; score: number }[];
  // Royale
  royale?: boolean;
  out?: string[];                      // who went out (a stage just ended), worst first
  stageOver?: boolean;                 // the round closed a stage
  places?: Record<string, number>;     // their final placing
  alive?: number;                      // how many are left
  winner?: string | null;              // set when the Royale is decided
}

// `match_over.royale`
export interface RoyaleResult {
  number: number;
  winner: string | null;
  placements: string[];  // every player id, best first
  size: number;
}

export const SIZE_LABEL: Record<string, string> = { small: "Small", medium: "Medium", large: "Large", huge: "Huge" };

// How a round mode is announced: its display name, colour and one short line.
export interface ModeLook { name: string; color: string; line: string }
const MODE_LOOK: Record<string, ModeLook> = {
  "Classic": { name: "Classic", color: "#19f0b0", line: "All power-ups are in play" },
  "Identity crisis": { name: "Identity crisis", color: "#a78bfa", line: "Just annoying power-ups" },
  "Sabotage": { name: "Sabotage", color: "#ff5c6c", line: "Only red power-ups" },
  "Drink and drive": { name: "Drink and drive", color: "#ff9f45", line: "Get a good attorney" },
  "Fatboy Slim": { name: "Fatboy Slim", color: "#ff6ad5", line: "Get fat or get thin" },
  [NO_POWERUPS_MODE]: { name: "No power-ups", color: "#4fa8ff", line: "" },
};
export function modeLook(mode: string | undefined | null): ModeLook {
  return (mode && MODE_LOOK[mode]) || { name: mode || "Classic", color: "#19f0b0", line: "" };
}
export const ROUND_MODE_NAMES = Object.keys(MODE_LOOK);

// Is this room one of the changing ones? (by its room id: "public:playnow", "public:royale#12")
export function dynamicKindOfRoom(room: string | undefined | null): DynamicKind | null {
  const m = /^public:(playnow|royale)(#|$)/.exec(room || "");
  return m ? (m[1] as DynamicKind) : null;
}

// An ordinal for a placing: 1st, 2nd, 3rd, 26th.
export function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return n + "th";
  return n + (["th", "st", "nd", "rd"][n % 10] || "th");
}
